import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Avatar } from '@/components/ui/Avatar';
import { useLivePerks } from '@/features/trusted-partners/hooks/useTrustedPartners';
import { ROUTES } from '@/lib/constants/routes';
import { initialsFromName } from '@/lib/utils/user.utils';
import { DASHBOARD_PREVIEW, DASHBOARD_STRINGS } from '@/lib/constants/dashboard-strings';

const S = DASHBOARD_STRINGS.LATEST_PERKS;

/**
 * The newest Trusted Partner offers in compact form (MYN-254), side column.
 * Each row opens the Startup Perks page, where the offer can be claimed: the
 * claim action and its click counting live on the full card only.
 */
export function DashboardLatestPerks() {
  const { data, isLoading, isError, refetch } = useLivePerks();
  const perks = (data ?? []).slice(0, DASHBOARD_PREVIEW.PERKS);

  return (
    <Card
      shadow="soft"
      hoverEffect={false}
      className="w-full p-6!"
      title={<span className="text-teal">{S.TITLE}</span>}
      subtitle={S.SUBTITLE}
      action={
        <Button asChild variant="link" className="shrink-0 text-sm whitespace-nowrap">
          <Link to={ROUTES.TRUSTED_PARTNERS_PERKS}>{DASHBOARD_STRINGS.VIEW_ALL}</Link>
        </Button>
      }
    >
      {isLoading && <Skeleton rows={3} className="h-10 w-full" />}
      {isError && <ErrorState description={S.LOAD_FAILED} onRetry={() => void refetch()} />}
      {!isLoading && !isError && perks.length === 0 && <p className="m-0 text-sm text-slate">{S.EMPTY}</p>}
      {perks.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {perks.map((perk) => (
            <li key={perk.id}>
              <Link
                to={ROUTES.TRUSTED_PARTNERS_PERKS}
                className="flex cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors hover:bg-page-bg focus-visible:ring-2 focus-visible:ring-green focus-visible:outline-none"
              >
                <Avatar
                  photoUrl={perk.partner.logoUrl ?? undefined}
                  initials={initialsFromName(perk.partner.businessName)}
                  // Larger so the partner's mark is recognisable (UX round 2, MYN-274).
                  size={40}
                />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-semibold text-navy">{perk.title}</span>
                  <span className="truncate text-xs text-slate">{perk.partner.businessName}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
